import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Fade,
  ButtonGroup,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormLabel,
  InputAdornment,
  Slider,
  Stack,
} from '@mui/material';
import { useWizard } from '../../context/WizardContext';
import ConditionalRender from '../common/ConditionalRender';
import CurrencyInput from '../common/CurrencyInput';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const EmploymentStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { employment, personal, uiState } = wizardData;
  
  // Initialize progress tracking
  const { trackFieldChange } = useProgressTracking({
    stepName: 'Employment Information',
    stepNumber: 2
  });
  
  // State for validation messages
  const [retirementAgeError, setRetirementAgeError] = React.useState('');
  const [partnerRetirementAgeError, setPartnerRetirementAgeError] = React.useState('');
  
  // Calculate current ages
  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  const currentAge = calculateAge(personal.client1.dateOfBirth);
  const partnerCurrentAge = calculateAge(personal.client2.dateOfBirth);

  const handleClient1Change = (field: 'isRetired' | 'currentIncome' | 'expectedRetirementAge' | 'hasPension' | 'pensionStartAge' | 'pensionBenefitAmount') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isRetired' || field === 'hasPension' ? event.target.value === 'true' : event.target.value;
    updateWizardData({
      employment: {
        ...employment,
        client1: {
          ...employment.client1,
          [field]: value,
        },
      },
    });
  };

  const handleClient2Change = (field: 'isRetired' | 'currentIncome' | 'expectedRetirementAge' | 'hasPension' | 'pensionStartAge' | 'pensionBenefitAmount') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isRetired' || field === 'hasPension' ? event.target.value === 'true' : event.target.value;
    updateWizardData({
      employment: {
        ...employment,
        client2: {
          ...employment.client2,
          [field]: value,
        },
      },
    });
  };

  const handleRetirementAgeChange = (event: Event, newValue: number | number[]) => {
    const age = newValue as number;
    updateWizardData({
      employment: {
        ...employment,
        client1: {
          ...employment.client1,
          expectedRetirementAge: age,
        },
      },
    });

    // Validate against current age
    if (currentAge > 0 && age < currentAge) {
      setRetirementAgeError(`Your planned retirement age cannot be earlier than your current age (${currentAge}).`);
    } else {
      setRetirementAgeError('');
    }
  };

  const handleRetirementAgeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const age = event.target.value === '' ? 67 : Number(event.target.value);
    updateWizardData({
      employment: {
        ...employment,
        client1: {
          ...employment.client1,
          expectedRetirementAge: age,
        },
      },
    });

    // Validate against current age
    if (currentAge > 0 && age < currentAge) {
      setRetirementAgeError(`Your planned retirement age cannot be earlier than your current age (${currentAge}).`);
    } else {
      setRetirementAgeError('');
    }
  };

  const handlePartnerRetirementAgeChange = (event: Event, newValue: number | number[]) => {
    const age = newValue as number;
    updateWizardData({
      employment: {
        ...employment,
        client2: {
          ...employment.client2,
          expectedRetirementAge: age,
        }
      },
    });

    // Validate against partner's current age
    if (partnerCurrentAge > 0 && age < partnerCurrentAge) {
      setPartnerRetirementAgeError(`${personal.client2.firstName}'s planned retirement age cannot be earlier than their current age (${partnerCurrentAge}).`);
    } else {
      setPartnerRetirementAgeError('');
    }
  };

  const handlePartnerRetirementAgeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const age = event.target.value === '' ? 67 : Number(event.target.value);
    updateWizardData({
      employment: {
        ...employment,
        client2: {
          ...employment.client2,
          expectedRetirementAge: age,
        }
      },
    });

    // Validate against partner's current age
    if (partnerCurrentAge > 0 && age < partnerCurrentAge) {
      setPartnerRetirementAgeError(`${personal.client2.firstName}'s planned retirement age cannot be earlier than their current age (${partnerCurrentAge}).`);
    } else {
      setPartnerRetirementAgeError('');
    }
  };

  // Only used to check if values are provided
  const hasCurrentIncome = employment.client1.currentIncome > 0;

  // Set default retirement age if not set
  React.useEffect(() => {
    if (hasCurrentIncome && !employment.client1.expectedRetirementAge) {
      updateWizardData({
        employment: {
          ...employment,
          client1: {
            ...employment.client1,
            expectedRetirementAge: 67,
          },
        },
      });
    }

    if (personal.hasPartner &&
        employment.client2.currentIncome > 0 &&
        !employment.client2.expectedRetirementAge) {
      updateWizardData({
        employment: {
          ...employment,
          client2: {
            ...employment.client2,
            expectedRetirementAge: 67,
          }
        },
      });
    }
  }, [hasCurrentIncome, employment.client2.currentIncome]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Work and Income
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box>
          <Typography variant="body1" paragraph>
            Are you currently retired?
          </Typography>
          <ButtonGroup variant="contained" sx={{ mt: 2 }}>
            <Button
              onClick={() => {
                updateWizardData({
                  employment: {
                    ...employment,
                    client1: {
                      ...employment.client1,
                      isRetired: true,
                    },
                  },
                  uiState: {
                    ...uiState,
                    hasAnsweredRetirementQuestion: true,
                  }
                });
              }}
              color={uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === true ? 'primary' : 'inherit'}
              sx={{ 
                minWidth: '120px',
                backgroundColor: uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === true ? undefined : 'grey.300',
                '&:hover': {
                  backgroundColor: uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === true ? undefined : 'grey.400',
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                updateWizardData({
                  employment: {
                    ...employment,
                    client1: {
                      ...employment.client1,
                      isRetired: false,
                    },
                  },
                  uiState: {
                    ...uiState,
                    hasAnsweredRetirementQuestion: true,
                  }
                });
              }}
              color={uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === false ? 'primary' : 'inherit'}
              sx={{ 
                minWidth: '120px',
                backgroundColor: uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === false ? undefined : 'grey.300',
                '&:hover': {
                  backgroundColor: uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === false ? undefined : 'grey.400',
                }
              }}
            >
              No
            </Button>
          </ButtonGroup>
        </Box>

        {/* Show message for retired users */}
        <ConditionalRender show={uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === true}>
          <Box>
            <Typography variant="body1" paragraph>
              Great, I hope you're enjoying your golden years.
            </Typography>
          </Box>
        </ConditionalRender>

        {/* Only show these questions if they're not retired */}
        <ConditionalRender show={uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === false}>
          <Box>
            <Typography variant="body1" paragraph>
              What is your current annual gross income?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <CurrencyInput
                  fullWidth
                  label="Annual Income"
                  value={employment.client1.currentIncome || ''}
                  onChange={(value) => {
                    updateWizardData({
                      employment: {
                        ...employment,
                        client1: {
                          ...employment.client1,
                          currentIncome: value,
                        },
                      },
                    });
                  }}
                />
              </Box>
            </Box>
          </Box>
        </ConditionalRender>

        {/* Show retirement age question only if not retired and has entered income */}
        <ConditionalRender show={uiState.hasAnsweredRetirementQuestion && employment.client1.isRetired === false && hasCurrentIncome}>
          <Box>
            <Typography variant="body1" paragraph>
              When do you plan to retire?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
              <Box sx={{ flex: '1 1 70%', minWidth: '250px' }}>
                <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    50
                  </Typography>
                  <Slider
                    value={typeof employment.client1.expectedRetirementAge === 'number' ? employment.client1.expectedRetirementAge : 67}
                    onChange={handleRetirementAgeChange}
                    aria-labelledby="retirement-age-slider"
                    valueLabelDisplay="auto"
                    step={1}
                    marks={[
                      { value: 67, label: '67' }
                    ]}
                    min={50}
                    max={85}
                    sx={{ mx: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    85
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ flex: '0 0 130px' }}>
                <TextField
                  value={employment.client1.expectedRetirementAge || 67}
                  size="medium"
                  onChange={handleRetirementAgeInputChange}
                  inputProps={{
                    step: 1,
                    min: 50,
                    max: 85,
                    type: 'number',
                    'aria-labelledby': 'retirement-age-slider',
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">years</InputAdornment>,
                  }}
                  error={!!retirementAgeError}
                />
              </Box>
            </Box>
            {retirementAgeError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {retirementAgeError}
              </Typography>
            )}
          </Box>
        </ConditionalRender>

        {personal.hasPartner && (
          <ConditionalRender show={uiState.hasAnsweredRetirementQuestion}>
            <Box sx={{ mt: 4 }}>
              <Typography variant="body1" paragraph>
                Now, is {personal.client2.firstName} retired?
              </Typography>
              
              <ButtonGroup variant="contained" sx={{ mt: 2 }}>
                <Button
                  onClick={() => updateWizardData({
                    employment: {
                      ...employment,
                      client2: {
                        ...employment.client2,
                        isRetired: true
                      }
                    },
                    uiState: {
                      ...uiState,
                      hasAnsweredPartnerRetirementQuestion: true,
                    }
                  })}
                  color={uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired === true ? 'primary' : 'inherit'}
                  sx={{
                    minWidth: '120px',
                    backgroundColor: uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired === true ? undefined : 'grey.300',
                    '&:hover': {
                      backgroundColor: uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired === true ? undefined : 'grey.400',
                    }
                  }}
                >
                  Yes
                </Button>
                <Button
                  onClick={() => updateWizardData({
                    employment: {
                      ...employment,
                      client2: {
                        ...employment.client2,
                        isRetired: false
                      }
                    },
                    uiState: {
                      ...uiState,
                      hasAnsweredPartnerRetirementQuestion: true,
                    }
                  })}
                  color={uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired === false ? 'primary' : 'inherit'}
                  sx={{
                    minWidth: '120px',
                    backgroundColor: uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired === false ? undefined : 'grey.300',
                    '&:hover': {
                      backgroundColor: uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired === false ? undefined : 'grey.400',
                    }
                  }}
                >
                  No
                </Button>
              </ButtonGroup>

              {/* For retired partner - Show message */}
              <ConditionalRender show={uiState.hasAnsweredPartnerRetirementQuestion && employment.client2.isRetired}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" paragraph>
                    Great, I hope {personal.client2.firstName} is enjoying retirement.
                  </Typography>
                </Box>
              </ConditionalRender>

              {/* For non-retired partner */}
              <ConditionalRender show={uiState.hasAnsweredPartnerRetirementQuestion && !employment.client2.isRetired}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" paragraph>
                    What is {personal.client2.firstName}'s current annual income?
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                      <CurrencyInput
                        fullWidth
                        label="Annual Income"
                        value={employment.client2.currentIncome || ''}
                        onChange={(value) => {
                          updateWizardData({
                            employment: {
                              ...employment,
                              client2: {
                                ...employment.client2,
                                currentIncome: value,
                              },
                            },
                          });
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Only show retirement age if income is provided */}
                  <ConditionalRender show={employment.client2.currentIncome > 0}>
                    <React.Fragment>
                      <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                        When does {personal.client2.firstName} plan to retire?
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        <Box sx={{ flex: '1 1 70%', minWidth: '250px' }}>
                          <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              50
                            </Typography>
                            <Slider
                              value={typeof employment.client2.expectedRetirementAge === 'number' ? employment.client2.expectedRetirementAge : 67}
                              onChange={handlePartnerRetirementAgeChange}
                              aria-labelledby="partner-retirement-age-slider"
                              valueLabelDisplay="auto"
                              step={1}
                              marks={[
                                { value: 67, label: '67' }
                              ]}
                              min={50}
                              max={85}
                              sx={{ mx: 2 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              85
                            </Typography>
                          </Stack>
                        </Box>
                        <Box sx={{ flex: '0 0 130px' }}>
                          <TextField
                            value={employment.client2.expectedRetirementAge || 67}
                            size="medium"
                            onChange={handlePartnerRetirementAgeInputChange}
                            inputProps={{
                              step: 1,
                              min: 50,
                              max: 85,
                              type: 'number',
                              'aria-labelledby': 'partner-retirement-age-slider',
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">years</InputAdornment>,
                            }}
                            error={!!partnerRetirementAgeError}
                          />
                        </Box>
                      </Box>
                      {partnerRetirementAgeError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          {partnerRetirementAgeError}
                        </Typography>
                      )}
                    </React.Fragment>
                  </ConditionalRender>
                </Box>
              </ConditionalRender>
            </Box>
          </ConditionalRender>
        )}
      </Box>
    </Box>
  );
};

export default EmploymentStep; 